import React, { useCallback, useEffect, useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import ResolveMultiSelect from 'components/ResolveMultiSelect';
import useDebounceSearch from 'hooks/useDebounceSearch';
import useMatchMedia from 'hooks/useMatchMedia';
import { Option } from 'models/Global';
import { getHasElementsInArray, getJoinedWordsWithComma } from 'utils';
import { SearchableFilterValue } from 'models/Filters';
import useSearchableFilters from 'hooks/useSearchableFilters';

import './SearchableMultiSelectFilter.styles.scss';

type SearchableMultiSelectFilterProps = {
  className?: string;
  value?: SearchableFilterValue;
  filterKey?: string;
  triggerTitle?: string;
  pillText?: string;
  hasCheckboxAll?: boolean;
  onSelect: (value: SearchableFilterValue) => void;
  getOptionsOnSearch: (searchString: string) => Promise<Option[]>;
};

const SearchableMultiSelectFilter: React.FC<
  SearchableMultiSelectFilterProps
> = (props) => {
  const {
    className,
    value,
    filterKey,
    triggerTitle,
    pillText,
    hasCheckboxAll = false,
    onSelect,
    getOptionsOnSearch,
  } = props;

  const { isLoading, options, onSearch, resetState } =
    useSearchableFilters(getOptionsOnSearch);

  const { t } = useTranslation();
  const isPhablet = useMatchMedia('isPhablet');

  const classes = classNames('searchable-multi-select-filter', className);

  const selected = useMemo(
    () => (Array.isArray(value) ? value : value ? [value] : []),
    [value],
  );

  const placeholder = useMemo(
    () =>
      triggerTitle ||
      (getHasElementsInArray(selected)
        ? getJoinedWordsWithComma(selected.map(({ label }) => label))
        : t('selectAnOption')),
    [triggerTitle, selected, t],
  );

  const mergedOptions = useMemo(() => {
    const optionsMap = new Map<string, Option>();

    const selectedButNotInOptions = selected.filter(
      (selected) => !options.some((option) => selected.value === option.value),
    );

    [...selectedButNotInOptions, ...options].forEach((option) => {
      optionsMap.set(option.value, option);
    });

    return Array.from(optionsMap.values());
  }, [options, selected]);

  const { isDebouncing, onSearchInputChange, resetSearchString } =
    useDebounceSearch({ onSearch });

  useEffect(() => {
    resetState();
    resetSearchString();
  }, [filterKey, resetSearchString, resetState]);

  const handleChange = useCallback(
    (selectedIds: string[]) =>
      onSelect(
        selectedIds.map((id) => ({
          value: id,
          label: mergedOptions.find(({ value }) => value === id)?.label,
        })),
      ),
    [mergedOptions, onSelect],
  );

  return (
    <ResolveMultiSelect
      key={filterKey}
      className={classes}
      pillText={pillText}
      searchable
      hasCheckbox
      hasCheckboxAll={hasCheckboxAll}
      value={selected.map(({ value }) => value)}
      options={mergedOptions}
      loading={isLoading || isDebouncing}
      renderAsPortal={!isPhablet}
      placeholder={placeholder}
      searchPlaceholder={t('search')}
      noDataTitle={t('typeToViewAvailableOptions')}
      emptySearchTitle={t('noDataFor')}
      onSearchChange={onSearchInputChange}
      onChange={handleChange}
    />
  );
};

export default SearchableMultiSelectFilter;
